import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {
  getComments,
  getInitialComment,
  getLatestSubmittedComment,
  getLatestSubmittedReply,
} from './thread-comment-accessors'
import {isLinkableAuthorLogin, type CommentAuthorIdentity} from './comment-identity'

export const ThreadGroupVariant = {
  /** Someone other than the current viewer has commented and nobody has replied yet */
  Commented: 'commented',
  /** Someone other than the current viewer has replied to a comment */
  Replied: 'replied',
  /** Someone has mentioned the current viewer in the latest comment */
  Mentioned: 'mentioned',
  /** The thread is resolved */
  Resolved: 'resolved',
  /** The current viewer has commented on the thread */
  YouCommented: 'you-commented',
  /** The current viewer has replied to a comment in the thread */
  YouReplied: 'you-replied',
  /** The thread is entirely pending */
  Pending: 'pending',
} as const

export type ThreadGroupVariant = (typeof ThreadGroupVariant)[keyof typeof ThreadGroupVariant]

export const ThreadGroupVariantLabels: Record<ThreadGroupVariant, string> = {
  [ThreadGroupVariant.Commented]: 'commented',
  [ThreadGroupVariant.Replied]: 'replied to a comment',
  [ThreadGroupVariant.Mentioned]: 'mentioned you',
  [ThreadGroupVariant.Resolved]: 'commented',
  [ThreadGroupVariant.YouCommented]: 'commented',
  [ThreadGroupVariant.YouReplied]: 'replied',
  [ThreadGroupVariant.Pending]: 'pending',
}

/**
 * Returns true when a comment's body `@`-mentions the given login.
 * Matches the raw body text on a username token boundary (case-insensitive), so
 * `@octocat` matches but `@octocat-bot` or an email-style `foo@octocat` does not.
 */
export function bodyMentionsViewer(comment: Comment | undefined, viewerLogin: string | undefined): boolean {
  if (!comment?.body || !viewerLogin) return false

  const escapedLogin = viewerLogin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const mentionPattern = new RegExp(`(^|[^a-z0-9-])@${escapedLogin}(?![a-z0-9-])`, 'i')
  return mentionPattern.test(comment.body)
}

/**
 * Classifies a thread into exactly one {@link ThreadGroupVariant}.
 *
 * Unresolved threads are evaluated in precedence order (first match wins):
 *  1. `pending` — the thread is entirely pending comments
 *  2. `mentioned` — the latest comment mentions the viewer
 *  3. `replied` — someone replied to an existing comment
 *  4. `commented` — everything else
 *
 * `viewerLogin` is `undefined` when logged out, in which case authorship and mention
 * checks are skipped and unresolved threads fall through to `commented`.
 */
export function classifyThread(thread: Thread, viewerLogin: string | undefined): ThreadGroupVariant {
  if (thread.isResolved) return ThreadGroupVariant.Resolved

  const allComments = getComments(thread)

  // Empty thread (shouldn't be possible?)
  if (allComments.length === 0) return ThreadGroupVariant.Commented

  const latestComment = getLatestSubmittedComment(thread)
  const latestReply = getLatestSubmittedReply(thread)

  // All comments are pending
  if (!latestComment) return ThreadGroupVariant.Pending

  // The freshest activity is a comment that mentions the viewer
  if (!latestComment.viewerDidAuthor && bodyMentionsViewer(latestComment, viewerLogin))
    return ThreadGroupVariant.Mentioned

  // Someone replied to a comment
  if (latestReply) return latestReply.viewerDidAuthor ? ThreadGroupVariant.YouReplied : ThreadGroupVariant.Replied

  // Unreplied comment
  return latestComment.viewerDidAuthor ? ThreadGroupVariant.YouCommented : ThreadGroupVariant.Commented
}

export const GHOST_LOGIN = 'ghost'

/**
 * Returns the login to attribute a thread's headline to for the given variant,
 * falling back to the ghost user when the thread or comment author no longer exists.
 */
export function getThreadAuthor(thread: Thread | null | undefined, variant: ThreadGroupVariant): CommentAuthorIdentity {
  if (!thread) return {login: GHOST_LOGIN, linkable: false}

  if (variant === ThreadGroupVariant.YouReplied || variant === ThreadGroupVariant.YouCommented)
    return {login: 'You', linkable: false}

  const login = getVariantComment(thread, variant)?.author?.login

  if (login === undefined) return {login: GHOST_LOGIN, linkable: true}

  return {login, linkable: isLinkableAuthorLogin(login)}
}

/**
 * Extracts the single comment whose author becomes the headline's "X" for the given variant.
 */
export function getVariantComment(thread: Thread, variant: ThreadGroupVariant): Comment | undefined {
  switch (variant) {
    case ThreadGroupVariant.Replied:
    case ThreadGroupVariant.YouReplied:
      return getLatestSubmittedReply(thread) ?? getLatestSubmittedComment(thread)
    case ThreadGroupVariant.Mentioned:
      // "X" is whoever wrote the comment that mentioned the viewer.
      // TODO: detect the mentioning comment explicitly; fall back to the latest comment for now.
      return getLatestSubmittedComment(thread)
    case ThreadGroupVariant.Resolved:
    case ThreadGroupVariant.Commented:
    case ThreadGroupVariant.YouCommented:
    default:
      return getInitialComment(thread)
  }
}
